/**
 * The income wire shapes — `Income`, `IncomeCreate`, `IncomeUpdate`, `ExternalProjectRef` from
 * `docs/api/paths/incomes.yaml` — and the row type the table and the form render them as.
 * Pure: safe to import from client components.
 */

import type { BexioInvoice } from '@/lib/api/bexio/types'
import type { ApiProjectOwner, ApiUserOwner } from '@/lib/api/ledger/types'
import { EntryStatusType, EntryStatusTypeValue, Page, strEnum } from '@/lib/api/types'
import { TableFilters, urlEnum, urlIds } from '@/lib/table-state'

export const IncomeType = strEnum(['expense_company_interest', 'income_intern_invoice', 'income_extern_invoice', 'income_extern_expense'])
export type IncomeTypeValue = keyof typeof IncomeType

/** `IncomeBexioStatus`: what the Bexio badge of an income shows. */
export const IncomeBexioStatus = strEnum(['draft', 'pending', 'paid', 'partial', 'canceled', 'unpaid', 'unsynced', 'unknown'])
export type IncomeBexioStatusValue = keyof typeof IncomeBexioStatus

export type ApiIncomePosition = {
  hours: number
  rate: number
  name: string
  positionCustomId?: string | null
}

export type ApiIncomeMeals = { breakfast: number; lunch: number; dinner: number }

/** How an external income names its project: the Bexio contact (and order); the server looks the project up or creates it. */
export type ExternalProjectRef = {
  bexioContactId: string
  bexioOrderId?: string | null
  name: string
}

/** The types a client may create — `expense_company_interest` is a booking type, never an income. */
export type WritableIncomeType = Exclude<IncomeTypeValue, 'expense_company_interest'>

export type ApiIncomeWritable = {
  name: string
  description?: string | null
  positions: ApiIncomePosition[]
  url?: string | null
  type: WritableIncomeType
  meals?: ApiIncomeMeals | null
  /** The calendar day, `YYYY-MM-DD` (`toApiDay`). */
  issued: string
  /** The member credited; the caller unless accountant. */
  user: number
  /** Exactly one of the two on create. */
  project?: number | null
  externalProject?: ExternalProjectRef | null
}

/** The project an income bills, as the list and the form render it. */
export type ApiIncomeProjectRef = ApiProjectOwner & {
  bexioContactId: string | null
  bexioOrderId: string | null
}

export type ApiIncome = Omit<ApiIncomeWritable, 'project' | 'type' | 'externalProject'> & {
  id: number
  /** `Money`: derived — Σ rate·hours of the positions. */
  amount: string
  type: IncomeTypeValue
  project: number
  status: EntryStatusTypeValue
  createdBy: number
  integrationId: string | null
  bexioInvoice: BexioInvoice | null
  userRef: ApiUserOwner
  projectRef: ApiIncomeProjectRef
  created: string
  updated: string
}

/** `POST /incomes`. */
export type IncomeCreate = ApiIncomeWritable

/** `PATCH /incomes/{id}` — every field optional. */
export type IncomeUpdate = Partial<ApiIncomeWritable>

/** An income as the table and the form render it: dates parsed, user and project next to it. */
export type IncomeRecord = Omit<ApiIncome, 'issued' | 'created' | 'updated'> & {
  issued: Date
  created: Date
  updated: Date
}

export type IncomeRow = {
  income: IncomeRecord
  user: ApiUserOwner
  project: ApiIncomeProjectRef
}

export const toIncomeRow = (income: ApiIncome): IncomeRow => ({
  income: {
    ...income,
    issued: new Date(income.issued),
    created: new Date(income.created),
    updated: new Date(income.updated),
  },
  user: income.userRef,
  project: income.projectRef,
})

/** `IncomePage`: one page of incomes, with the sum of `amount` over every filtered row (#844). */
export type IncomePage = Page<ApiIncome> & { totals: { amount: string } }

/** The filters of `GET /incomes`. */
export type IncomesQuery = {
  year?: number
  /** Incomes credited to these members. */
  users?: number[]
  projects?: number[]
  status?: EntryStatusTypeValue[]
  types?: IncomeTypeValue[]
  bexioStatuses?: IncomeBexioStatusValue[]
  q?: string
}

/** `IncomesQuery` as the contract's query params (design.md §8.2). */
export const incomesParams = (q: IncomesQuery) => ({
  year: q.year,
  user: q.users,
  project: q.projects,
  status: q.status,
  type: q.types,
  bexioStatus: q.bexioStatuses,
  q: q.q,
})

/**
 * The sort keys of `GET /incomes` that the incomes table offers, a subset of
 * `IncomeRepository.sortable` (`backend/app/repositories/income.py`): a key the backend drops is a
 * 422 for the page.
 */
export const INCOME_SORTABLE = ['issued', 'created', 'name', 'type', 'status', 'amount', 'user', 'project'] as const

/** `GET /incomes`'s order when the URL names none (`IncomeRepository.default_sort`), as table sort state. */
export const INCOME_DEFAULT_SORTING = [{ id: 'income_issued', desc: true }]

/**
 * The incomes table's URL filters (`f.income_*`) as an API query, key by key: a key not listed
 * here never reaches the API, and a value the API would refuse is dropped.
 */
export const incomesQueryOf = (filters: TableFilters): IncomesQuery => ({
  status: urlEnum(EntryStatusType, filters.income_status),
  types: urlEnum(IncomeType, filters.income_type),
  users: urlIds(filters.income_user),
  projects: urlIds(filters.income_project),
  bexioStatuses: urlEnum(IncomeBexioStatus, filters.income_bexio_status),
})
